"use client";
import { TODOS } from "@/lib/idiomas";
import { useState, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import { resolverPais } from "@/lib/geo/pais";
import { getAb } from "@/lib/ab";
import api from "@/lib/api";

const SUPPORTED = TODOS;

// Degradé de la colección completa (mismo que FULL_GRADIENT en las landings):
// representa a TODA la colección, no a un libro puntual. Es el fondo por defecto
// del botón en las landings genéricas. Cada libro tiene su color propio, así que
// no hay un color sólido "neutro de marca" — el degradé es la identidad común.
const COLLECTION_GRADIENT =
  "linear-gradient(120deg, #d0ac22 0%, #e588b5 20%, #81d0e1 40%, #95ca44 60%, #c45646 80%, #e29f53 100%)";

/**
 * Bloque de alta a la newsletter (consentimiento para recibir novedades).
 * Reutilizable en cualquier landing. Copy genérico por defecto (claves common
 * `newsletter_*`), customizable por caso vía props: ej. la preventa de Escalas
 * Ilustradas pasa su propio título/subtítulo para mencionar el libro.
 *
 * Botón: pastilla BLANCA con borde-gradiente (mismo patrón que GradientButton /
 * el resto de los CTAs del sitio). `buttonBackground` es el color/degradé del BORDE:
 * en genéricas, el degradé de la colección (default); en una landing de libro, el
 * color sólido de ese libro (ej. /libro/ei pasa el violeta de Escalas).
 *
 * POST /api/newsletter { email, locale, source } — idempotente, NO envía mails:
 * solo registra el opt-in. `source` identifica de dónde se anotó (ej. libro_ei,
 * home, libros). El locale se deriva del path.
 */
export default function NewsletterSignup({
  source = "site",
  initialEmail = "",
  accentColor = "#ffffff",
  buttonBackground = COLLECTION_GRADIENT,
  background = "#191917",
  title,
  subtitle,
  placeholder,
  button,
  loadingLabel,
  success,
  consent,
  errorInvalid,
  errorGeneric,
}) {
  const { t, i18n } = useTranslation();
  const pathname = usePathname();
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const locale = useMemo(() => {
    const seg = (pathname?.split("/")?.[1] || "").toLowerCase();
    if (SUPPORTED.includes(seg)) return seg;
    const lng = (i18n.language || "").toLowerCase().split("-")[0];
    return SUPPORTED.includes(lng) ? lng : "es";
  }, [pathname, i18n.language]);

  // Copy: las props tienen prioridad; si no se pasan, usa las claves genéricas de common.
  const txt = {
    title: title ?? t("newsletter_title"),
    subtitle: subtitle ?? t("newsletter_subtitle"),
    placeholder: placeholder ?? t("newsletter_placeholder"),
    button: button ?? t("newsletter_button"),
    loading: loadingLabel ?? t("newsletter_loading"),
    success: success ?? t("newsletter_success"),
    consent: consent ?? t("newsletter_consent"),
    errorInvalid: errorInvalid ?? t("newsletter_error_invalid"),
    errorGeneric: errorGeneric ?? t("newsletter_error_generic"),
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    const value = String(email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError(txt.errorInvalid);
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const cc = resolverPais({ fallback: "" });
      // Anotarse acá SIEMPRE incluye la muestra (sin checkbox: lo dice el texto fijo de abajo).
      // `ab`: con qué variante de las pruebas A/B pidió la muestra (null si no está en ninguna).
      // La muestra vende días después y por mail: es lo que une esa compra con lo que vio.
      await api.post("/api/newsletter", { email: value, locale, source, cc, wants_muestra: true, ab: getAb() });
      setStatus("success");
    } catch (err) {
      console.error("[newsletter] signup error", err);
      setError(txt.errorGeneric);
      setStatus("error");
    }
  };

  return (
    <section style={{ background, padding: "55px 20px" }}>
      <div style={{ maxWidth: 460, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ color: "#fff", fontSize: 22, fontWeight: 700, margin: "0 0 8px" }}>{txt.title}</h2>
        <p style={{ color: "#bbb", fontSize: 14, lineHeight: 1.5, margin: "0 0 22px" }}>{txt.subtitle}</p>
        {status === "success" ? (
          <p style={{ color: accentColor, fontSize: 16, fontWeight: 700, margin: 0 }}>{txt.success}</p>
        ) : (
          <>
            <form onSubmit={onSubmit} style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={txt.placeholder}
                aria-label={txt.placeholder}
                disabled={status === "loading"}
                style={{ flex: "1 1 220px", minWidth: 0, maxWidth: 300, height: 48, padding: "0 18px", borderRadius: 999, border: "1px solid #444", background: "#222", color: "#fff", fontSize: 15, boxSizing: "border-box" }}
              />
              {/* Pastilla blanca con borde-gradiente: mismo patrón que el resto de los CTAs
                  del sitio (GradientButton de _collection). El degradé/color va SOLO en el
                  borde de 3px; el relleno es blanco con texto oscuro, como los demás botones. */}
              <div style={{ background: buttonBackground, padding: 3, borderRadius: 999, flexShrink: 0, display: "inline-flex" }}>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-btn-animate="y"
                  className="btn btn-mod btn-circle"
                  style={{ height: 42, padding: "0 26px", borderRadius: 999, border: "none", background: "#fff", color: "#111", fontSize: 13, fontWeight: 700, letterSpacing: "0.085em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", whiteSpace: "nowrap", cursor: "pointer", opacity: status === "loading" ? 0.7 : 1 }}
                >
                  {status === "loading" ? txt.loading : txt.button}
                </button>
              </div>
            </form>
            <p style={{ color: "#777", fontSize: 12, lineHeight: 1.5, margin: "12px 0 0" }}>{txt.consent}</p>
          </>
        )}
        {status === "error" && error && (
          <p style={{ color: "#e88", fontSize: 13, margin: "10px 0 0" }}>{error}</p>
        )}
      </div>
    </section>
  );
}
